import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Slide,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { formatTemplateDeadlineOffsetLabel } from "../../utils/taskDeadlineDisplay";
import TeamAssigneePickerDialog from "./TeamAssigneePickerDialog";
import TaskTemplateFieldOptionsDialog from "./TaskTemplateFieldOptionsDialog";
import TaskFieldOptionsDialog from "./TaskFieldOptionsDialog";
import TaskChatPanel from "./TaskChatPanel";
import TaskFormFileTile from "./TaskFormFileTile";
import TaskFormMediaPreviewModal from "./TaskFormMediaPreviewModal";
import ConnectionBanner from "../ConnectionBlocker/ConnectionBanner";
import {
  getConnectionStatus,
  retryConnection,
  onConnectionChange,
  onDisconnectionChange,
  onReconnectingChange,
  onReconnectFailedChange,
} from "../../utils/socketHandler";
import connectionMonitor from "../../utils/connectionMonitor";
import { validateTaskTemplateFile } from "../../utils/taskTemplateMediaValidation";
import { fetchTaskFieldOptions, uploadTaskFile } from "../../redux/Slice/Tasks/tasksSlice";
import { fetchTaskTemplateFieldOptions, uploadTaskTemplateFile } from "../../redux/Slice/Tasks/taskTemplatesSlice";
import {
  DEFAULT_PRIORITY_OPTIONS,
  DEFAULT_TYPE_OPTIONS,
  ensureFieldOptionPresent,
  getFieldOptionChipSx,
  getFieldOptionValue,
  mergeStatusOptionsWithDefaults,
} from "../../utils/taskTemplateChipStyles";
import {
  DEFAULT_RECURRING_RESET_TIME,
  DEFAULT_INTERVAL_START_TIME,
  dayjsFromTimeString,
  getDailyCreateTimes,
  getDailyCheckpointScheduleTimes,
  isDailyCheckpointResetAfterLastCheckpoint,
  formatDailyTimesSummary,
} from "../../utils/recurringDailyTimes";
import { sanitizeRecurringConfig } from "../../utils/recurringConfigSanitize";
import {
  taskFormAutocompleteSx,
  taskFormBodyTextSx,
  taskFormChipSx,
  taskFormColumnSplitChatSx,
  taskFormColumnSplitFormSx,
  taskFormDarkContainerSx,
  taskFormFieldLabelRowSx,
  taskFormFieldLabelSx,
  taskFormFieldLabelIconSx,
  taskFormFieldLabelWithIconSx,
  taskFormFieldRowSx,
  taskFormFieldRowStartSx,
  taskFormFloatingButtonSx,
  taskFormInlineButtonSx,
  taskFormSectionTitleWithIconSx,
  taskFormNameInputSx,
  taskFormPaperSx,
  taskFormPrimaryButtonSx,
  taskFormRecurrenceCadenceToggleSx,
  taskFormRecurrenceDialogLabelSx,
  taskFormRecurrenceDialogTextSx,
  taskFormRecurrenceInlineNumberSx,
  taskFormRecurrenceInnerSx,
  taskFormRecurrenceOptionLabelSx,
  taskFormRecurrencePanelSx,
  taskFormRecurrencePickerSlotProps,
  taskFormRecurrenceSoftBoxPadding,
  taskFormRecurrenceSubheadingSx,
  taskFormRecurrenceSummarySx,
  taskFormRecurrenceWeekdayLabelSx,
  taskFormScrollContentSx,
  taskFormSecondaryButtonSx,
  taskFormSectionPadding,
  taskFormSectionSx,
  taskFormSectionTitleSx,
  taskFormSoftBoxSx,
  taskFormStickyFooterSx,
} from "../../utils/taskFormStyles";

/** Bottom-sheet feel: the modal slides up from below (Bitrix-style). */
const SlideUpTransition = React.forwardRef(function SlideUpTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const createChecklistTemplate = (title = "Checklist #1") => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title,
  expanded: true,
  items: [],
});

const DEFAULT_VISIBLE_FIELDS = { type: false, status: false, priority: false };

const DEFAULT_ENABLED_SECTIONS = {
  files: false,
  checklists: false,
  participants: false,
  recurring: false,
};

const mapChecklistsFromInitial = (checklists) => {
  if (!Array.isArray(checklists) || checklists.length === 0) return [];
  return checklists.map((x) => ({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    title: x.title || "Checklist",
    expanded: true,
    items: Array.isArray(x.items)
      ? x.items.map((item) => ({
          id: `i_${Math.random()}`,
          text: item.text || "",
          done: Boolean(item.done),
        }))
      : [],
  }));
};

const hasChecklistSectionData = (checklists) => {
  if (!Array.isArray(checklists) || checklists.length === 0) return false;
  return checklists.some(
    (checklist) =>
      String(checklist?.title || "").trim() !== "" ||
      (Array.isArray(checklist?.items) &&
        checklist.items.some((item) => String(item?.text || "").trim() !== ""))
  );
};

const resolveEnabledSectionsFromInitial = (initialData, { fileCount, checklistRows, mode }) => {
  const isBlankCreate = mode === "create" && initialData == null;
  if (isBlankCreate) return { ...DEFAULT_ENABLED_SECTIONS };

  const filesExplicit = initialData.enabledSections?.files === true;
  const checklistsExplicit = initialData.enabledSections?.checklists === true;

  return {
    files: filesExplicit || fileCount > 0,
    checklists: checklistsExplicit || hasChecklistSectionData(checklistRows),
    participants: initialData.enabledSections?.participants === true,
    recurring: initialData.enabledSections?.recurring === true,
  };
};

const resolveVisibleFieldsFromInitial = (initialData, mode) => {
  const isBlankCreate = mode === "create" && initialData == null;
  if (isBlankCreate) return { ...DEFAULT_VISIBLE_FIELDS };
  return {
    type: Boolean(String(initialData?.templateType || "").trim()),
    status: Boolean(String(initialData?.status || "").trim()),
    priority: Boolean(String(initialData?.priority || "").trim()),
  };
};

/** Draft / persisted task–like object includes recurrence from templates or saved tasks */
const hasRecurringPayload = (data) =>
  !!(data && data.enabledSections?.recurring === true && data.recurringConfig != null);

/** MUI X DatePicker + AdapterDayjs require a Dayjs instance (or null); Date/string breaks isValid(). */
const toPickerDayjs = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "object" && typeof v.isValid === "function") {
    try {
      return v.isValid() ? v : null;
    } catch {
      return null;
    }
  }
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

/** ISO deadline from server task row (detail fetch or realtime). */
const resolveServerDeadlineIso = (data) => {
  if (!data) return null;
  const raw = data.deadlineAt ?? data.deadline;
  if (raw == null || raw === "") return null;
  const d = dayjs(raw);
  return d.isValid() ? d.toISOString() : null;
};

const DEFAULT_DEADLINE_HOUR = 17;
const DEFAULT_DEADLINE_MINUTE = 0;

/** Task deadline date field — day/month/year (templates use offset picker, not this). */
const TASK_DEADLINE_DATE_FORMAT = "DD/MM/YYYY";

const mergeDeadlineDate = (dateValue, currentDeadline) => {
  const nextDate = toPickerDayjs(dateValue);
  if (!nextDate) return null;
  const current = toPickerDayjs(currentDeadline);
  if (current?.isValid()) {
    return nextDate.hour(current.hour()).minute(current.minute()).second(0).millisecond(0);
  }
  return nextDate
    .hour(DEFAULT_DEADLINE_HOUR)
    .minute(DEFAULT_DEADLINE_MINUTE)
    .second(0)
    .millisecond(0);
};

const mergeDeadlineTime = (timeValue, currentDeadline) => {
  const nextTime = toPickerDayjs(timeValue);
  if (!nextTime?.isValid()) return currentDeadline;
  const current = toPickerDayjs(currentDeadline);
  if (current?.isValid()) {
    return current.hour(nextTime.hour()).minute(nextTime.minute()).second(0).millisecond(0);
  }
  return dayjs()
    .hour(nextTime.hour())
    .minute(nextTime.minute())
    .second(0)
    .millisecond(0);
};

/** Relative default deadline (Bitrix-style) — templates only. */
const DEADLINE_OFFSET_PRESETS = [
  { label: "1 day", amount: 1, unit: "days" },
  { label: "3 days", amount: 3, unit: "days" },
  { label: "1 week", amount: 1, unit: "weeks" },
  { label: "2 weeks", amount: 2, unit: "weeks" },
];

const DEADLINE_OFFSET_CUSTOM_UNITS = [
  { value: "days", label: "days" },
  { value: "hours", label: "hours" },
  { value: "minutes", label: "minutes" },
];

const ALLOWED_OFFSET_UNITS = new Set(["minutes", "hours", "days", "weeks"]);

const normalizeOffsetInput = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const amount = Number(raw.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const unit = ALLOWED_OFFSET_UNITS.has(String(raw.unit)) ? String(raw.unit) : "days";
  return { amount: Math.floor(amount), unit };
};

const offsetEquals = (a, b) => {
  const na = normalizeOffsetInput(a);
  const nb = normalizeOffsetInput(b);
  if (!na && !nb) return true;
  if (!na || !nb) return false;
  return na.amount === nb.amount && na.unit === nb.unit;
};

const defaultRecurringConfig = () => ({
  cadence: "daily",
  dailyEveryDaysEnabled: true,
  dailyEveryDays: 1,
  dailyEveryMonthsEnabled: false,
  dailyEveryMonths: 1,
  weeklyDays: [],
  weeklyInterval: 1,
  monthlyMode: "dayOfMonth",
  monthlyDayOfMonth: 1,
  monthlyOrdinal: "First",
  monthlyWeekday: "Monday",
  monthlyInterval: 1,
  yearlyMode: "specificDate",
  yearlyDay: 1,
  yearlyMonth: "January",
  yearlyOrdinal: "First",
  yearlyWeekday: "Monday",
  yearlyMonthByOrdinal: "January",
  yearlyIntervalMonths: 12,
  recurringStartDate: dayjs(),
  recurringStopMode: "manually",
  recurringStopAfterRuns: 3,
  recurringStopExactDate: dayjs().add(5, "day"),
  createTime: DEFAULT_RECURRING_RESET_TIME,
  dailyRunMode: "single",
  createTimes: [DEFAULT_RECURRING_RESET_TIME],
  dailyIntervalStartTime: DEFAULT_RECURRING_RESET_TIME,
  dailyIntervalRunsPerDay: 3,
  dailyIntervalHours: 4,
  includeWeekendsAndHolidays: true,
});

/** API / Mongo JSON yields plain dates for recurrence fields — coerce before DatePicker binding */
const normalizeRecurringDraft = (raw) => {
  const base = defaultRecurringConfig();
  if (!raw || typeof raw !== "object") {
    return { ...base };
  }
  const out = { ...base, ...raw };
  const s =
    raw.recurringStartDate != null && raw.recurringStartDate !== ""
      ? dayjs(raw.recurringStartDate)
      : null;
  out.recurringStartDate = s && s.isValid() ? s : base.recurringStartDate;
  const e =
    raw.recurringStopExactDate != null && raw.recurringStopExactDate !== ""
      ? dayjs(raw.recurringStopExactDate)
      : null;
  out.recurringStopExactDate = e && e.isValid() ? e : base.recurringStopExactDate;

  return sanitizeRecurringConfig(out, "load");
};

const toRecurringCalendarDate = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : value;
};

const prepareRecurringDraftForSave = (draft) => {
  const out = sanitizeRecurringConfig(
    {
      ...draft,
      dailyEveryMonthsEnabled: false,
    },
    "save"
  );
  if (out.recurringStartDate != null) {
    out.recurringStartDate = toRecurringCalendarDate(out.recurringStartDate);
  }
  if (out.recurringStopExactDate != null) {
    out.recurringStopExactDate = toRecurringCalendarDate(out.recurringStopExactDate);
  }
  out.recurrenceUtcOffsetMinutes = -new Date().getTimezoneOffset();
  try {
    out.recurrenceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* ignore */
  }
  return out;
};

const validateDailyRecurringDraft = (draft) => {
  if ((draft.cadence || "daily") !== "daily") return { ok: true };
  const mode = draft.dailyRunMode || "single";
  if (mode === "slots") {
    const times = Array.isArray(draft.createTimes) ? draft.createTimes : [];
    if (times.length < 1) {
      return { ok: false, message: "Add at least one time slot for daily recurrence." };
    }
    if (times.length > 12) {
      return { ok: false, message: "Daily recurrence supports up to 12 time slots per day." };
    }
    const resetTime = String(draft.createTime || "").trim();
    if (!resetTime) {
      return { ok: false, message: "Choose a task reset time for end of day." };
    }
    if (!isDailyCheckpointResetAfterLastCheckpoint(draft)) {
      return {
        ok: false,
        message: "Task reset time must be after the last time slot of the day.",
      };
    }
  }
  if (mode === "single") {
    const time = String(draft.createTime || "").trim();
    if (!time) {
      return { ok: false, message: "Choose a reset time for once per day." };
    }
  }
  if (mode === "interval") {
    const runs = Number(draft.dailyIntervalRunsPerDay);
    const hours = Number(draft.dailyIntervalHours);
    if (!Number.isFinite(runs) || runs < 2 || runs > 24) {
      return { ok: false, message: "Runs per day must be between 2 and 24." };
    }
    if (!Number.isFinite(hours) || hours < 1 || hours > 23) {
      return { ok: false, message: "Hours between runs must be between 1 and 23." };
    }
    const generated = getDailyCreateTimes(draft);
    if (generated.length < 2) {
      return {
        ok: false,
        message: "Interval settings must produce at least 2 runs within the same day.",
      };
    }
    const resetTime = String(draft.createTime || "").trim();
    if (!resetTime) {
      return { ok: false, message: "Choose a task reset time for end of day." };
    }
    if (!isDailyCheckpointResetAfterLastCheckpoint(draft)) {
      return {
        ok: false,
        message: "Task reset time must be after the last interval run of the day.",
      };
    }
  }
  return { ok: true };
};

const ordinalOptions = ["First", "Second", "Third", "Fourth", "Last"];
const weekdayOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Repeat every N months under Yearly cadence (12 = once per year on selected month/date). */
const YEARLY_INTERVAL_MONTH_OPTIONS = [2, 3, 4, 5, 6, 8, 9, 10, 11, 12];
const detectMediaType = (mime = "") => {
  const lower = String(mime).toLowerCase();
  if (lower.startsWith("image/")) return "images";
  if (lower.startsWith("video/")) return "videos";
  if (lower.startsWith("audio/")) return "audio";
  return "documents";
};

const RecurringTaskDialog = ({ open, onClose, value, onSave }) => {
  const [draft, setDraft] = useState(() => normalizeRecurringDraft(value));

  const minEndDate = React.useMemo(() => {
    const startD = toPickerDayjs(draft.recurringStartDate);
    return startD?.isValid() ? startD.startOf("day") : undefined;
  }, [draft.recurringStartDate]);

  React.useEffect(() => {
    if (open) {
      setDraft(normalizeRecurringDraft(value));
    }
  }, [open, value]);

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const numberInputSx = {
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };
  const handleNumberChange = (key) => (e) => {
    const value_ = e.target.value;
    if (value_ === "") {
      update({ [key]: "" });
      return;
    }
    if (/^\d+$/.test(value_)) {
      update({ [key]: Number(value_) });
    }
  };

  const getCreateTimeValue = (timeStr) => dayjsFromTimeString(timeStr || draft.createTime);

  const dailyRunMode = draft.dailyRunMode || "single";
  const dailyTimesList =
    Array.isArray(draft.createTimes) && draft.createTimes.length
      ? draft.createTimes
      : dailyRunMode === "slots"
        ? ["6:00 pm"]
        : [draft.createTime || DEFAULT_RECURRING_RESET_TIME];

  const addDailyTimeSlot = () => {
    if (dailyTimesList.length >= 12) {
      toast.error("Up to 12 time slots per day.");
      return;
    }
    update({ createTimes: [...dailyTimesList, "12:00 pm"], dailyRunMode: "slots" });
  };

  const updateDailyTimeSlot = (index, newValue) => {
    const next = dailyTimesList.map((t, i) =>
      i === index
        ? newValue && dayjs(newValue).isValid()
          ? dayjs(newValue).format("h:mm a")
          : t
        : t
    );
    update({ createTimes: next, dailyRunMode: "slots" });
  };

  const removeDailyTimeSlot = (index) => {
    if (dailyTimesList.length <= 1) {
      toast.error("At least one time slot is required.");
      return;
    }
    const next = dailyTimesList.filter((_, i) => i !== index);
    update({ createTimes: next, dailyRunMode: "slots" });
  };

  const isCadence = (x) => draft.cadence === x;
  const showGlobalCreateTime = !isCadence("daily");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: taskFormPaperSx() }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.4rem" } }}>
          Recurring task
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={taskFormScrollContentSx}>
        <Stack spacing={1.75} sx={taskFormRecurrencePanelSx}>
          <Typography sx={taskFormRecurrenceDialogLabelSx}>Reset task schedule:</Typography>
          <ToggleButtonGroup
            exclusive
            value={draft.cadence}
            onChange={(_, value_) => value_ && update({ cadence: value_ })}
            fullWidth
            size="medium"
            sx={taskFormRecurrenceCadenceToggleSx}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">Yearly</ToggleButton>
          </ToggleButtonGroup>

          {isCadence("daily") && (
            <>
              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                <FormControlLabel
                  sx={taskFormRecurrenceOptionLabelSx}
                  control={
                    <Checkbox
                      checked={draft.dailyEveryDaysEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          update({ dailyEveryDaysEnabled: true, dailyEveryMonthsEnabled: false });
                        } else {
                          update({ dailyEveryDaysEnabled: false });
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>Interval:</Typography>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.dailyEveryDays ?? ""}
                        onChange={handleNumberChange("dailyEveryDays")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                        disabled={!draft.dailyEveryDaysEnabled}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>day</Typography>
                    </Box>
                  }
                />
              </Box>
              {!draft.dailyEveryDaysEnabled && (
                <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                  <FormControlLabel
                    sx={taskFormRecurrenceOptionLabelSx}
                    control={<Checkbox checked disabled />}
                    label={
                      <Typography sx={taskFormRecurrenceDialogTextSx}>
                        Daily — task resets every calendar day.
                      </Typography>
                    }
                  />
                </Box>
              )}

              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                <Typography sx={taskFormRecurrenceSubheadingSx}>Runs per day</Typography>
                <RadioGroup
                  value={dailyRunMode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    if (mode === "single") {
                      setDraft((prev) => {
                        const next = {
                          ...prev,
                          dailyRunMode: "single",
                          createTime: prev.createTime || DEFAULT_RECURRING_RESET_TIME,
                        };
                        delete next.createTimes;
                        return next;
                      });
                      return;
                    }
                    if (mode === "slots") {
                      update({
                        dailyRunMode: "slots",
                        createTimes:
                          dailyTimesList.length > 1
                            ? dailyTimesList
                            : ["6:00 pm", "7:00 pm"],
                        createTime: DEFAULT_RECURRING_RESET_TIME,
                      });
                      return;
                    }
                    setDraft((prev) => {
                      const next = {
                        ...prev,
                        dailyRunMode: "interval",
                        dailyIntervalStartTime:
                          prev.dailyIntervalStartTime || DEFAULT_INTERVAL_START_TIME,
                        createTime: DEFAULT_RECURRING_RESET_TIME,
                        dailyIntervalRunsPerDay: prev.dailyIntervalRunsPerDay || 3,
                        dailyIntervalHours: prev.dailyIntervalHours || 4,
                      };
                      delete next.createTimes;
                      return next;
                    });
                  }}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Once per day"
                    sx={taskFormRecurrenceOptionLabelSx}
                  />
                  {dailyRunMode === "single" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        pl: 4.25,
                        pb: 0.5,
                      }}
                    >
                      <Typography sx={taskFormRecurrenceDialogLabelSx}>Reset at:</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          value={getCreateTimeValue(draft.createTime)}
                          onChange={(newValue) =>
                            update({
                              createTime:
                                newValue && dayjs(newValue).isValid()
                                  ? dayjs(newValue).format("h:mm a")
                                  : draft.createTime,
                            })
                          }
                          ampm
                          views={["hours", "minutes"]}
                          minutesStep={5}
                          slotProps={{
                            textField: {
                              ...taskFormRecurrencePickerSlotProps.textField,
                              sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  )}
                  <FormControlLabel
                    value="slots"
                    control={<Radio />}
                    label="Specific time slots"
                    sx={taskFormRecurrenceOptionLabelSx}
                  />
                  <FormControlLabel
                    value="interval"
                    control={<Radio />}
                    label="Repeat during the day (after start time)"
                    sx={taskFormRecurrenceOptionLabelSx}
                  />
                </RadioGroup>

                {dailyRunMode === "slots" && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {dailyTimesList.map((slot, index) => (
                      <Box
                        key={`daily-slot-${index}`}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            value={getCreateTimeValue(slot)}
                            onChange={(newValue) => updateDailyTimeSlot(index, newValue)}
                            ampm
                            views={["hours", "minutes"]}
                            minutesStep={5}
                            slotProps={{
                              textField: {
                                ...taskFormRecurrencePickerSlotProps.textField,
                                sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                              },
                            }}
                          />
                        </LocalizationProvider>
                        <IconButton
                          size="medium"
                          onClick={() => removeDailyTimeSlot(index)}
                          disabled={dailyTimesList.length <= 1}
                          aria-label="Remove time slot"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="medium"
                      startIcon={<AddIcon />}
                      onClick={addDailyTimeSlot}
                      sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    >
                      Add time slot
                    </Button>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        pt: 0.5,
                      }}
                    >
                      <Typography sx={taskFormRecurrenceDialogLabelSx}>Task reset at:</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          value={getCreateTimeValue(draft.createTime)}
                          onChange={(newValue) =>
                            update({
                              createTime:
                                newValue && dayjs(newValue).isValid()
                                  ? dayjs(newValue).format("h:mm a")
                                  : draft.createTime,
                            })
                          }
                          ampm
                          views={["hours", "minutes"]}
                          minutesStep={5}
                          slotProps={{
                            textField: {
                              ...taskFormRecurrencePickerSlotProps.textField,
                              sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Typography sx={{ ...taskFormRecurrenceDialogTextSx, color: "text.secondary" }}>
                      Checkpoints run at each slot above; the task rolls to the next day at reset
                      time ({getDailyCheckpointScheduleTimes(draft).join(", ")}).
                    </Typography>
                  </Stack>
                )}

                {dailyRunMode === "interval" && (
                  <Stack spacing={1.25} sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogLabelSx}>Start at:</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          value={getCreateTimeValue(
                            draft.dailyIntervalStartTime || DEFAULT_INTERVAL_START_TIME
                          )}
                          onChange={(newValue) =>
                            update({
                              dailyIntervalStartTime:
                                newValue && dayjs(newValue).isValid()
                                  ? dayjs(newValue).format("h:mm a")
                                  : draft.dailyIntervalStartTime,
                            })
                          }
                          ampm
                          views={["hours", "minutes"]}
                          minutesStep={5}
                          slotProps={{
                            textField: {
                              ...taskFormRecurrencePickerSlotProps.textField,
                              sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>Run</Typography>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.dailyIntervalRunsPerDay ?? ""}
                        onChange={handleNumberChange("dailyIntervalRunsPerDay")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                        inputProps={{ min: 2, max: 24 }}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>time(s), every</Typography>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.dailyIntervalHours ?? ""}
                        onChange={handleNumberChange("dailyIntervalHours")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                        inputProps={{ min: 1, max: 23 }}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>hour(s)</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        pt: 0.5,
                      }}
                    >
                      <Typography sx={taskFormRecurrenceDialogLabelSx}>Task reset at:</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          value={getCreateTimeValue(draft.createTime)}
                          onChange={(newValue) =>
                            update({
                              createTime:
                                newValue && dayjs(newValue).isValid()
                                  ? dayjs(newValue).format("h:mm a")
                                  : draft.createTime,
                            })
                          }
                          ampm
                          views={["hours", "minutes"]}
                          minutesStep={5}
                          slotProps={{
                            textField: {
                              ...taskFormRecurrencePickerSlotProps.textField,
                              sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Typography sx={{ ...taskFormRecurrenceDialogTextSx, color: "text.secondary" }}>
                      Checkpoints: {getDailyCreateTimes(draft).join(", ")}. Full schedule:{" "}
                      {getDailyCheckpointScheduleTimes(draft).join(", ")}.
                    </Typography>
                  </Stack>
                )}
              </Box>
            </>
          )}

          {isCadence("weekly") && (
            <>
              <Box
                sx={{
                  ...taskFormSoftBoxSx,
                  ...taskFormRecurrenceSoftBoxPadding,
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 0.5,
                }}
              >
                {weekdayOptions.map((day) => {
                  const checked = draft.weeklyDays.includes(day);
                  return (
                    <FormControlLabel
                      key={day}
                      sx={{ m: 0, ...taskFormRecurrenceOptionLabelSx }}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(e) =>
                            update({
                              weeklyDays: e.target.checked
                                ? [...draft.weeklyDays, day]
                                : draft.weeklyDays.filter((x) => x !== day),
                            })
                          }
                        />
                      }
                      label={
                        <Typography sx={taskFormRecurrenceWeekdayLabelSx}>
                          {day.slice(0, 3)}
                        </Typography>
                      }
                    />
                  );
                })}
              </Box>
              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                <FormControlLabel
                  sx={taskFormRecurrenceOptionLabelSx}
                  control={<Checkbox checked />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>Interval:</Typography>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.weeklyInterval ?? ""}
                        onChange={handleNumberChange("weeklyInterval")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>week</Typography>
                    </Box>
                  }
                />
              </Box>
            </>
          )}

          {isCadence("monthly") && (
            <>
              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                <RadioGroup
                  value={draft.monthlyMode}
                  onChange={(e) => update({ monthlyMode: e.target.value })}
                >
                  <FormControlLabel
                    value="dayOfMonth"
                    control={<Radio />}
                    sx={taskFormRecurrenceOptionLabelSx}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={taskFormRecurrenceDialogTextSx}>
                          On this day of the month:
                        </Typography>
                        <TextField
                          size="medium"
                          type="number"
                          value={draft.monthlyDayOfMonth ?? ""}
                          onChange={handleNumberChange("monthlyDayOfMonth")}
                          sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                        />
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="ordinalWeekday"
                    control={<Radio />}
                    sx={taskFormRecurrenceOptionLabelSx}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <TextField
                          size="medium"
                          select
                          value={draft.monthlyOrdinal}
                          onChange={(e) => update({ monthlyOrdinal: e.target.value })}
                          sx={{ minWidth: 120 }}
                        >
                          {ordinalOptions.map((x) => (
                            <MenuItem key={x} value={x}>
                              {x}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="medium"
                          select
                          value={draft.monthlyWeekday}
                          onChange={(e) => update({ monthlyWeekday: e.target.value })}
                          sx={{ minWidth: 140 }}
                        >
                          {weekdayOptions.map((x) => (
                            <MenuItem key={x} value={x}>
                              {x}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
                <FormControlLabel
                  sx={taskFormRecurrenceOptionLabelSx}
                  control={<Checkbox checked />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>Interval:</Typography>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.monthlyInterval ?? ""}
                        onChange={handleNumberChange("monthlyInterval")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>month</Typography>
                    </Box>
                  }
                />
              </Box>
            </>
          )}

          {isCadence("yearly") && (
            <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding }}>
              <RadioGroup
                value={draft.yearlyMode}
                onChange={(e) => update({ yearlyMode: e.target.value })}
              >
                <FormControlLabel
                  value="specificDate"
                  control={<Radio />}
                  sx={taskFormRecurrenceOptionLabelSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <TextField
                        size="medium"
                        type="number"
                        value={draft.yearlyDay ?? ""}
                        onChange={handleNumberChange("yearlyDay")}
                        sx={{ ...taskFormRecurrenceInlineNumberSx, ...numberInputSx }}
                      />
                      <TextField
                        size="medium"
                        select
                        value={draft.yearlyMonth}
                        onChange={(e) => update({ yearlyMonth: e.target.value })}
                        sx={{ minWidth: 150 }}
                      >
                        {monthOptions.map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="ordinalWeekdayMonth"
                  control={<Radio />}
                  sx={taskFormRecurrenceOptionLabelSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <TextField
                        size="medium"
                        select
                        value={draft.yearlyOrdinal}
                        onChange={(e) => update({ yearlyOrdinal: e.target.value })}
                        sx={{ minWidth: 110 }}
                      >
                        {ordinalOptions.map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="medium"
                        select
                        value={draft.yearlyWeekday}
                        onChange={(e) => update({ yearlyWeekday: e.target.value })}
                        sx={{ minWidth: 135 }}
                      >
                        {weekdayOptions.map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="medium"
                        select
                        value={draft.yearlyMonthByOrdinal}
                        onChange={(e) => update({ yearlyMonthByOrdinal: e.target.value })}
                        sx={{ minWidth: 155 }}
                      >
                        {monthOptions.map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  }
                />
              </RadioGroup>
              <Box sx={{ ...taskFormSoftBoxSx, ...taskFormRecurrenceSoftBoxPadding, mt: 1.25 }}>
                <FormControlLabel
                  sx={taskFormRecurrenceOptionLabelSx}
                  control={<Checkbox checked />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>Repeat every:</Typography>
                      <TextField
                        size="medium"
                        select
                        value={draft.yearlyIntervalMonths ?? 12}
                        onChange={(e) =>
                          update({ yearlyIntervalMonths: Number(e.target.value) || 12 })
                        }
                        sx={{ minWidth: 120 }}
                      >
                        {YEARLY_INTERVAL_MONTH_OPTIONS.map((n) => (
                          <MenuItem key={n} value={n}>
                            {n} month{n === 1 ? "" : "s"}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  }
                />
              </Box>
            </Box>
          )}

          <Typography sx={{ ...taskFormRecurrenceDialogLabelSx, mt: 0.5 }}>Recurrence start:</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={TASK_DEADLINE_DATE_FORMAT}
              value={toPickerDayjs(draft.recurringStartDate)}
              onChange={(newValue) =>
                update({ recurringStartDate: toPickerDayjs(newValue) || dayjs() })
              }
              slotProps={{
                textField: {
                  ...taskFormRecurrencePickerSlotProps.textField,
                  placeholder: "DD/MM/YYYY",
                  sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 240 },
                },
              }}
            />
          </LocalizationProvider>

          <Typography sx={{ ...taskFormRecurrenceDialogLabelSx, mt: 0.5 }}>Recurrence stop:</Typography>
          <RadioGroup
            value={draft.recurringStopMode}
            onChange={(e) => update({ recurringStopMode: e.target.value })}
          >
            <Box sx={{ ...taskFormSoftBoxSx, p: 1.25, borderRadius: 1 }}>
              <FormControlLabel
                value="manually"
                control={<Radio />}
                label="Manually"
                sx={taskFormRecurrenceOptionLabelSx}
              />
            </Box>
            <Box sx={{ ...taskFormSoftBoxSx, p: 1.25, borderRadius: 1 }}>
              <FormControlLabel
                value="after"
                control={<Radio />}
                sx={taskFormRecurrenceOptionLabelSx}
                label={
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={taskFormRecurrenceDialogTextSx}>After</Typography>
                    <TextField
                        size="medium"
                      type="number"
                      value={draft.recurringStopAfterRuns ?? ""}
                      onChange={handleNumberChange("recurringStopAfterRuns")}
                        sx={{ width: 80, ...numberInputSx }}
                      />
                      <Typography sx={taskFormRecurrenceDialogTextSx}>
                        scheduled occurrence(s)
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ ...taskFormRecurrenceDialogTextSx, color: "text.secondary", pl: 4.5 }}
                    >
                      Each reset time counts as one (including multiple runs per day).
                    </Typography>
                  </Box>
                }
              />
            </Box>
            <Box sx={{ ...taskFormSoftBoxSx, p: 1.25, borderRadius: 1 }}>
              <FormControlLabel
                value="exactDate"
                control={<Radio />}
                sx={taskFormRecurrenceOptionLabelSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={taskFormRecurrenceDialogTextSx}>Exact date:</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format={TASK_DEADLINE_DATE_FORMAT}
                        value={toPickerDayjs(draft.recurringStopExactDate)}
                        minDate={minEndDate}
                        onChange={(newValue) =>
                          update({
                            recurringStopExactDate: toPickerDayjs(newValue) || dayjs(),
                          })
                        }
                        slotProps={{
                          textField: {
                            ...taskFormRecurrencePickerSlotProps.textField,
                            placeholder: "DD/MM/YYYY",
                            sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 180 },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                }
              />
            </Box>
          </RadioGroup>

          {showGlobalCreateTime && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
              <Typography sx={taskFormRecurrenceDialogLabelSx}>Reset task at:</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={getCreateTimeValue()}
                onChange={(newValue) =>
                  update({
                    createTime:
                      newValue && dayjs(newValue).isValid()
                        ? dayjs(newValue).format("h:mm a")
                        : draft.createTime,
                  })
                }
                ampm
                views={["hours", "minutes"]}
                minutesStep={5}
                slotProps={{
                  textField: {
                      ...taskFormRecurrencePickerSlotProps.textField,
                      sx: { ...taskFormRecurrencePickerSlotProps.textField.sx, width: 150 },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            <Typography sx={taskFormRecurrenceDialogLabelSx}>
              Allow task reset on Sunday and holidays:
            </Typography>
            <TextField
              select
              size="medium"
              value={draft.includeWeekendsAndHolidays ? "yes" : "no"}
              onChange={(e) =>
                update({ includeWeekendsAndHolidays: e.target.value === "yes" })
              }
              sx={{
                width: 110,
                "& .MuiSelect-select": {
                  fontSize: { xs: "0.975rem", sm: "1rem" },
                  py: 1.1,
                },
              }}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={taskFormStickyFooterSx}>
        <Button onClick={onClose} sx={taskFormSecondaryButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={taskFormPrimaryButtonSx}
          onClick={() => {
            if (draft.recurringStopMode === "exactDate") {
              const startD = toPickerDayjs(draft.recurringStartDate)?.startOf("day");
              const endD = toPickerDayjs(draft.recurringStopExactDate)?.startOf("day");
              if (startD?.isValid() && endD?.isValid() && endD.isBefore(startD)) {
                toast.error("Recurrence end date cannot be before the recurrence start date.");
                return;
              }
            }
            const dailyCheck = validateDailyRecurringDraft(draft);
            if (!dailyCheck.ok) {
              toast.error(dailyCheck.message);
              return;
            }
            onSave(prepareRecurringDraftForSave(draft));
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const buildRecurringSummary = (recurring) => {
  if (!recurring) return "";
  const stopMode = recurring.recurringStopMode || recurring.recurringStopType;
  const stopAfterCount =
    recurring.recurringStopAfterRuns ?? recurring.recurringStopRuns;
  const stopText =
    stopMode === "manually" || stopMode === "manual"
      ? "Stop: manually."
      : stopMode === "after" || stopMode === "afterRuns"
      ? `Stop: after ${stopAfterCount} scheduled occurrence(s) (each reset time counts).`
      : `Stop: ${dayjs(recurring.recurringStopExactDate).isValid() ? dayjs(recurring.recurringStopExactDate).format("MMM D") : "exact date"}.`;
  if (recurring.cadence === "daily") {
    let cadenceText = "Daily";
    if (recurring.dailyEveryDaysEnabled) {
      cadenceText = `Every ${recurring.dailyEveryDays} day(s)`;
    }
    return `${cadenceText}. ${formatDailyTimesSummary(recurring)}. ${stopText}`;
  }
  if (recurring.cadence === "weekly") {
    const days = recurring.weeklyDays.length
      ? recurring.weeklyDays.map((x) => x.slice(0, 3)).join(", ")
      : "selected days";
    return `Every ${recurring.weeklyInterval} week(s) on ${days}. Time: ${recurring.createTime}. ${stopText}`;
  }
  if (recurring.cadence === "monthly") {
    if (recurring.monthlyMode === "dayOfMonth") {
      return `Monthly on day ${recurring.monthlyDayOfMonth}, every ${recurring.monthlyInterval} month(s). Time: ${recurring.createTime}. ${stopText}`;
    }
    return `Monthly on ${recurring.monthlyOrdinal} ${recurring.monthlyWeekday}, every ${recurring.monthlyInterval} month(s). Time: ${recurring.createTime}. ${stopText}`;
  }
  const yearlyInterval = Number(recurring.yearlyIntervalMonths) || 12;
  const yearlyEveryText =
    yearlyInterval === 12
      ? "Every 12 months (yearly)"
      : `Every ${yearlyInterval} month(s)`;
  if (recurring.yearlyMode === "specificDate") {
    return `${yearlyEveryText} on day ${recurring.yearlyDay} of ${recurring.yearlyMonth}. Time: ${recurring.createTime}. ${stopText}`;
  }
  return `${yearlyEveryText} on ${recurring.yearlyOrdinal} ${recurring.yearlyWeekday} of ${recurring.yearlyMonthByOrdinal}. Time: ${recurring.createTime}. ${stopText}`;
};

const ChecklistBuilderDialog = ({ open, onClose, onSave, initialChecklists, canDelete = true }) => {
  const [localChecklists, setLocalChecklists] = useState(initialChecklists);

  React.useEffect(() => {
    setLocalChecklists(initialChecklists);
  }, [initialChecklists, open]);

  const updateChecklist = (checklistId, updater) => {
    setLocalChecklists((prev) =>
      prev.map((checklist) =>
        checklist.id === checklistId ? { ...checklist, ...updater(checklist) } : checklist
      )
    );
  };

  const addChecklist = () => {
    setLocalChecklists((prev) => [
      ...prev,
      createChecklistTemplate(`Checklist #${prev.length + 1}`),
    ]);
  };

  const addItem = (checklistId) => {
    updateChecklist(checklistId, (checklist) => ({
      items: [
        ...checklist.items,
        { id: `i_${Math.random()}`, text: "", done: false },
      ],
    }));
  };

  const removeItem = (checklistId, itemId) => {
    updateChecklist(checklistId, (checklist) => ({
      items: checklist.items.filter((item) => item.id !== itemId),
    }));
  };

  const updateItem = (checklistId, itemId, patch) => {
    updateChecklist(checklistId, (checklist) => ({
      items: checklist.items.map((item) =>
        item.id === itemId ? { ...item, ...patch } : item
      ),
    }));
  };

  const removeChecklist = (checklistId) => {
    setLocalChecklists((prev) => prev.filter((x) => x.id !== checklistId));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: taskFormPaperSx() }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.4rem" } }}>
          Checklist Builder
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={taskFormScrollContentSx}>
        <Stack spacing={2}>
          {localChecklists.map((checklist) => (
            <Box
              key={checklist.id}
              sx={{
                ...taskFormSectionSx,
                p: taskFormSectionPadding,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <TextField
                  variant="standard"
                  value={checklist.title}
                  onChange={(e) =>
                    updateChecklist(checklist.id, () => ({ title: e.target.value }))
                  }
                  sx={{ flex: 1 }}
                />
                {canDelete && (
                  <IconButton size="small" onClick={() => removeChecklist(checklist.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Stack spacing={0.75}>
                {checklist.items.map((item) => (
                  <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={item.done}
                      onChange={(e) =>
                        updateItem(checklist.id, item.id, { done: e.target.checked })
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Checklist item"
                      value={item.text}
                      onChange={(e) =>
                        updateItem(checklist.id, item.id, { text: e.target.value })
                      }
                    />
                    {canDelete && (
                      <IconButton size="small" onClick={() => removeItem(checklist.id, item.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
              <Button
                startIcon={<AddIcon />}
                sx={{ textTransform: "none", mt: 1 }}
                onClick={() => addItem(checklist.id)}
              >
                Add item
              </Button>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} sx={{ textTransform: "none", alignSelf: "flex-start" }} onClick={addChecklist}>
            Add another checklist
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={taskFormStickyFooterSx}>
        <Button onClick={onClose} sx={taskFormSecondaryButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={taskFormPrimaryButtonSx}
          onClick={() => {
            onSave(localChecklists);
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const templateDeadlinePresetButtonSx = {
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.95rem",
  py: 1.05,
  px: 2,
  width: "100%",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  color: "text.primary",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "divider",
    boxShadow: "none",
  },
};

/**
 * Bitrix-style relative-deadline picker for task templates (popover box).
 * Stores { amount, unit } or null when no default deadline is set.
 */
const TemplateDeadlineOffsetPicker = ({ value, onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const normalized = normalizeOffsetInput(value);
  const matchedPreset = DEADLINE_OFFSET_PRESETS.find((preset) =>
    offsetEquals(preset, normalized)
  );
  const isCustom = !!normalized && !matchedPreset;

  const [customAmount, setCustomAmount] = useState(isCustom ? String(normalized.amount) : "");
  const [customUnit, setCustomUnit] = useState(
    isCustom && normalized.unit !== "weeks" ? normalized.unit : "days"
  );

  useEffect(() => {
    if (isCustom) {
      setCustomAmount(String(normalized.amount));
      setCustomUnit(normalized.unit === "weeks" ? "days" : normalized.unit);
    } else {
      setCustomAmount("");
    }
  }, [normalized?.amount, normalized?.unit, isCustom]);

  const triggerLabel = normalized
    ? formatTemplateDeadlineOffsetLabel(normalized)
    : "No deadline";

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handlePreset = (preset) => {
    if (disabled) return;
    onChange?.({ amount: preset.amount, unit: preset.unit });
    handleClose();
  };

  const handleClear = () => {
    if (disabled) return;
    setCustomAmount("");
    onChange?.(null);
    handleClose();
  };

  const commitCustom = (amountRaw, unitRaw) => {
    if (disabled) return;
    const amount = Math.floor(Number(amountRaw));
    if (!Number.isFinite(amount) || amount <= 0) return;
    const unit = ALLOWED_OFFSET_UNITS.has(String(unitRaw)) ? String(unitRaw) : "days";
    onChange?.({ amount, unit });
  };

  const handleAmountChange = (event) => {
    const next = event.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(next);
    if (next !== "") commitCustom(next, customUnit);
  };

  const handleUnitChange = (event) => {
    const next = event.target.value;
    setCustomUnit(next);
    if (customAmount !== "") commitCustom(customAmount, next);
  };

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Button
        variant="text"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={
          <EventNoteOutlinedIcon sx={{ fontSize: "1.15rem", color: "primary.main" }} />
        }
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          color: "primary.main",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          borderRadius: "10px",
          px: 1.5,
          py: 0.85,
          justifyContent: "flex-start",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
          },
          "&.Mui-disabled": {
            color: "text.disabled",
            bgcolor: "action.disabledBackground",
          },
        }}
      >
        {triggerLabel}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 300,
              maxWidth: "calc(100vw - 24px)",
              borderRadius: "12px",
              boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 1.75 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
                lineHeight: 1.45,
                flex: 1,
              }}
            >
              Specify the default deadline for new tasks created from this template.
            </Typography>
            <HelpOutlineIcon sx={{ fontSize: "1rem", color: "text.disabled", mt: 0.15 }} />
          </Stack>

          <Stack spacing={0.85}>
            {DEADLINE_OFFSET_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="outlined"
                color="inherit"
                disabled={disabled}
                onClick={() => handlePreset(preset)}
                sx={templateDeadlinePresetButtonSx}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>

          <Typography
            sx={{
              mt: 1.75,
              mb: 0.85,
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            Custom:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder=""
              value={customAmount}
              onChange={handleAmountChange}
              disabled={disabled}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{
                width: 72,
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            />
            <TextField
              select
              size="small"
              value={customUnit}
              onChange={handleUnitChange}
              disabled={disabled}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            >
              {DEADLINE_OFFSET_CUSTOM_UNITS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {normalized && (
            <Button
              fullWidth
              variant="text"
              color="inherit"
              disabled={disabled}
              onClick={handleClear}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              No deadline
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

const TaskTemplateCreateModal = ({
  open,
  onClose,
  roleOptions,
  mode = "create",
  initialData = null,
  onSubmit,
  onSwitchToEdit,
  submitting = false,
  canSwitchToEdit = false,
  entityType = "template",
  splitWithChat = false,
  showTemplatesPicker = false,
  canManageFieldOptions = false,
  templatePickerRows = [],
  onApplyTemplateFromPicker,
  taskAccessFlags = null,
  onCompleteTask,
}) => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth?.user);
  const templateFieldOptions = useSelector((state) => state.taskTemplates.fieldOptions);
  const taskFieldOptions = useSelector((state) => state.tasks.fieldOptions);
  const fieldOptions = entityType === "task" ? taskFieldOptions : templateFieldOptions;
  const fileInputRef = useRef(null);
  const isViewMode = mode === "view";
  const canEdit = !isViewMode;
  const isTaskCreateMode = entityType === "task" && mode === "create";
  const taskParticipantReadOnly =
    entityType === "task" &&
    taskAccessFlags &&
    !taskAccessFlags.canFullEdit &&
    !taskAccessFlags.canEditChecklists;
  /** Full task fields: create mode is editable; saved tasks require full task edit. */
  const taskDetailFieldEdit =
    entityType !== "task"
      ? canEdit
      : isViewMode || taskParticipantReadOnly
        ? false
        : isTaskCreateMode
          ? canEdit
          : Boolean(taskAccessFlags?.canFullEdit) && canEdit;
  const taskChecklistEdit =
    entityType !== "task"
      ? canEdit
      : !isViewMode &&
        canEdit &&
        (!taskAccessFlags ||
          !!(taskAccessFlags.canFullEdit || taskAccessFlags.canEditChecklists));
  const checklistOnlyAutosave =
    entityType === "task" &&
    mode === "edit" &&
    taskChecklistEdit &&
    !taskDetailFieldEdit;
  const canSubmit =
    canEdit && (entityType !== "task" || taskDetailFieldEdit || taskChecklistEdit);

  const showTemplateFieldOpts = entityType === "template" && canManageFieldOptions;
  const showTaskFieldOpts = entityType === "task" && canManageFieldOptions;
  const currentUserName = authUser?.name || "Current User";
  const currentUserOwner = useMemo(() => {
    const id = String(authUser?._id || authUser?.id || "").trim();
    if (!id) return null;
    return { id, type: "teammember", name: currentUserName };
  }, [authUser?._id, authUser?.id, currentUserName]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [manageTypeOpen, setManageTypeOpen] = useState(false);
  const [manageStatusOpen, setManageStatusOpen] = useState(false);
  const [managePriorityOpen, setManagePriorityOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [deadline, setDeadline] = useState(null);
  /** Templates only: relative default deadline (Bitrix-style). */
  const [deadlineOffset, setDeadlineOffset] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const [templatesPickerOpen, setTemplatesPickerOpen] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [checklists, setChecklists] = useState([]);
  const [checklistAutosaving, setChecklistAutosaving] = useState(false);
  const [openChecklistBuilder, setOpenChecklistBuilder] = useState(false);
  const [openRecurringDialog, setOpenRecurringDialog] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState(null);
  const latestChecklistsRef = useRef(checklists);
  const checklistAutosavingRef = useRef(false);
  const queuedChecklistAutosaveRef = useRef(null);
  /** True after user edits deadline pickers — save uses local value; otherwise server value. */
  const deadlineTouchedByUserRef = useRef(false);
  const serverFieldsSyncRef = useRef({ taskId: null, revision: null });
  /** Avoid re-init deadline/status/checklists when same task receives a shallow prop refresh. */
  const lastMainInitTaskIdRef = useRef(null);
  const [enabledSections, setEnabledSections] = useState({ ...DEFAULT_ENABLED_SECTIONS });
  const [visibleFields, setVisibleFields] = useState({ ...DEFAULT_VISIBLE_FIELDS });

  useEffect(() => {
    if (!open) setPreviewFile(null);
  }, [open]);

  /** User cleared recurrence during this modal session (persisted tasks only) */
  const [recurringRemovedThisSession, setRecurringRemovedThisSession] = useState(false);

  const isDirectBlankTaskCreate =
    entityType === "task" && mode === "create" && initialData == null;

  /** Templates always manage recurrence; tasks only after template prefills or persisted recurrence state */
  const showRecurringTaskControls = useMemo(() => {
    if (entityType === "template") return true;
    if (entityType !== "task") return false;
    if (isDirectBlankTaskCreate) return false;
    const persistedRecurringEligible =
      mode !== "create" &&
      Boolean(initialData?._id) &&
      hasRecurringPayload(initialData) &&
      !recurringRemovedThisSession;
    return (
      (enabledSections.recurring === true && recurringConfig != null) ||
      (mode === "create" &&
        initialData != null &&
        !initialData._id &&
        hasRecurringPayload(initialData)) ||
      persistedRecurringEligible
    );
  }, [
    entityType,
    mode,
    isDirectBlankTaskCreate,
    enabledSections.recurring,
    recurringConfig,
    initialData,
    recurringRemovedThisSession,
  ]);

  useEffect(() => {
    if (!open) return;
    setRecurringRemovedThisSession(false);
  }, [open, initialData?._id, mode]);

  useEffect(() => {
    if (!showRecurringTaskControls) {
      setOpenRecurringDialog(false);
    }
  }, [showRecurringTaskControls]);

  useEffect(() => {
    latestChecklistsRef.current = checklists;
  }, [checklists]);

  useEffect(() => {
    if (!open) return;
    if (entityType === "task") {
      dispatch(fetchTaskFieldOptions());
    } else {
      dispatch(fetchTaskTemplateFieldOptions());
    }
  }, [open, dispatch, entityType]);

  const typeChoices = useMemo(
    () =>
      ensureFieldOptionPresent(
        fieldOptions.typeOptions?.length ? fieldOptions.typeOptions : DEFAULT_TYPE_OPTIONS,
        templateType,
        "type"
      ),
    [fieldOptions.typeOptions, templateType]
  );

  const statusChoices = useMemo(
    () =>
      ensureFieldOptionPresent(
        mergeStatusOptionsWithDefaults(fieldOptions.statusOptions || []),
        status,
        "status"
      ),
    [fieldOptions.statusOptions, status]
  );

  const priorityChoices = useMemo(
    () =>
      ensureFieldOptionPresent(
        fieldOptions.priorityOptions?.length ? fieldOptions.priorityOptions : DEFAULT_PRIORITY_OPTIONS,
        priority,
        "priority"
      ),
    [fieldOptions.priorityOptions, priority]
  );

  /** Sync cron/automation-driven fields when server task updates (realtime or parent refresh). */
  useEffect(() => {
    if (!open) {
      deadlineTouchedByUserRef.current = false;
      serverFieldsSyncRef.current = { taskId: null, revision: null };
      lastMainInitTaskIdRef.current = null;
      return;
    }
    if (entityType !== "task" || mode === "create" || !initialData?._id) return;

    const taskId = String(initialData._id);
    const revision = [
      initialData.updatedAt,
      initialData.recurringState?.lastCycleAt,
      initialData.recurringState?.nextRunAt,
      initialData.deadlineAt ?? initialData.deadline,
      initialData.status,
    ].join("|");

    const prev = serverFieldsSyncRef.current;
    if (prev.taskId !== taskId) {
      deadlineTouchedByUserRef.current = false;
      serverFieldsSyncRef.current = { taskId, revision };
      return;
    }
    if (prev.revision === revision) return;
    serverFieldsSyncRef.current = { taskId, revision };

    if (!deadlineTouchedByUserRef.current) {
      setDeadline(toPickerDayjs(initialData.deadlineAt ?? initialData.deadline ?? null));
    }
    setStatus(initialData.status || "");
    setChecklists(mapChecklistsFromInitial(initialData.checklists));
    if (initialData.recurringConfig && typeof initialData.recurringConfig === "object") {
      setRecurringConfig(normalizeRecurringDraft(initialData.recurringConfig));
    }
  }, [open, entityType, mode, initialData]);

  useEffect(() => {
    if (!open) return;
    const defaultOwners = currentUserOwner ? [currentUserOwner] : [];
    const shouldForceCurrentOwner = mode === "create";
    if (!initialData) {
      lastMainInitTaskIdRef.current = null;
      setName("");
      setDescription("");
      setTemplateType("");
      setStatus("");
      setPriority("");
      setAssignees([]);
      setDeadline(null);
      setDeadlineOffset(null);
      setUploadedFiles([]);
      setParticipants([]);
      setChecklists([]);
      setRecurringConfig(null);
      setOwners(defaultOwners);
      setEnabledSections({ ...DEFAULT_ENABLED_SECTIONS });
      setVisibleFields({ ...DEFAULT_VISIBLE_FIELDS });
      return;
    }
    const taskId = initialData._id ? String(initialData._id) : null;
    const isSameTaskRehydrate =
      entityType === "task" && taskId && lastMainInitTaskIdRef.current === taskId;
    if (!isSameTaskRehydrate) {
      lastMainInitTaskIdRef.current = taskId;
    }
    setName(initialData.name || "");
    setDescription(initialData.description || "");
    setTemplateType(initialData.templateType || "");
    if (!isSameTaskRehydrate) {
      setStatus(initialData.status || "");
    }
    setPriority(initialData.priority || "");
    if (shouldForceCurrentOwner) {
      setOwners(defaultOwners);
    } else if (Array.isArray(initialData.owners) && initialData.owners.length > 0) {
      setOwners(initialData.owners);
    } else if (initialData.owner?.id) {
      setOwners([
        {
          id: String(initialData.owner.id),
          type: "teammember",
          name: initialData.owner.name || "",
        },
      ]);
    } else {
      setOwners([]);
    }
    setAssignees(Array.isArray(initialData.assignees) ? initialData.assignees : []);
    if (!isSameTaskRehydrate && !deadlineTouchedByUserRef.current) {
      setDeadline(toPickerDayjs(initialData.deadlineAt ?? initialData.deadline ?? null));
    }
    setDeadlineOffset(
      entityType === "template" ? normalizeOffsetInput(initialData.deadlineOffset) : null
    );
    const mappedFiles = Array.isArray(initialData.files)
      ? initialData.files.map((f, idx) => ({
          id: f.filename || `file_${idx}`,
          name: f.originalName || f.name || f.filename,
          url: f.url,
          type: f.mimetype || "",
          filename: f.filename,
          originalName: f.originalName || f.name || f.filename,
          size: f.size || 0,
          mediaType: f.mediaType,
        }))
      : [];
    setUploadedFiles(mappedFiles);
    const mappedChecklists = mapChecklistsFromInitial(initialData.checklists);
    if (!isSameTaskRehydrate) {
      setChecklists(mappedChecklists);
    }
    setRecurringConfig(
      initialData.recurringConfig && typeof initialData.recurringConfig === "object"
        ? normalizeRecurringDraft(initialData.recurringConfig)
        : null
    );
    const sections = resolveEnabledSectionsFromInitial(initialData, {
      fileCount: mappedFiles.length,
      checklistRows: mappedChecklists,
      mode,
    });
    setEnabledSections(
      entityType === "task"
        ? sections
        : {
            ...sections,
            participants: initialData.enabledSections?.participants === true,
          }
    );
    setVisibleFields(resolveVisibleFieldsFromInitial(initialData, mode));
    if (entityType === "task") {
      setParticipants([]);
    } else {
      setParticipants(
        shouldForceCurrentOwner ? [] : Array.isArray(initialData.participants) ? initialData.participants : []
      );
    }
  }, [open, initialData, mode, currentUserOwner, entityType]);

  const ownerDisplayText = useMemo(() => {
    if (owners.length > 0) {
      return owners.map((item) => item.name).filter(Boolean).join(", ");
    }
    return mode === "create" ? currentUserName : "—";
  }, [owners, mode, currentUserName]);

  const optionLabel = (option) => getFieldOptionValue(option);

  const handleAutocompleteValueChange = (setter) => (_, newValue) => {
    if (newValue === null || newValue === undefined) return;
    setter(typeof newValue === "string" ? newValue : getFieldOptionValue(newValue));
  };

  const isFieldOptionEqualToValue = (option, value) =>
    getFieldOptionValue(option).toLowerCase() === getFieldOptionValue(value).toLowerCase();

  const renderFieldOption = (kind, options) => (props, option) => {
    const value = getFieldOptionValue(option);
    return (
      <Box component="li" {...props} key={`${kind}_${value}`}>
        <Chip size="small" label={value} sx={getFieldOptionChipSx(value, options, kind)} />
      </Box>
    );
  };

  const filteredTemplatePickerRows = useMemo(() => {
    const q = templateSearchQuery.trim().toLowerCase();
    if (!q) return templatePickerRows || [];
    return (templatePickerRows || []).filter((r) => String(r.name || "").toLowerCase().includes(q));
  }, [templatePickerRows, templateSearchQuery]);

  const completedChecklistMeta = useMemo(
    () =>
      checklists.map((checklist) => {
        const total = checklist.items.length;
        const completed = checklist.items.filter((x) => x.done).length;
        return { ...checklist, total, completed };
      }),
    [checklists]
  );

  const assigneeSelectedItems = useMemo(() => assignees, [assignees]);

  const handleUploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    const uploaded = [];
    for (const file of files) {
      const validation = validateTaskTemplateFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        continue;
      }
      try {
        const uploadThunk = entityType === "task" ? uploadTaskFile : uploadTaskTemplateFile;
        const saved = await dispatch(uploadThunk(file)).unwrap();
        uploaded.push({
          id: saved.filename || `${Date.now()}_${file.name}`,
          name: saved.originalName || file.name,
          url: saved.url,
          type: saved.mimetype || file.type,
          filename: saved.filename,
          originalName: saved.originalName || file.name,
          size: saved.size || file.size,
          mediaType: saved.mediaType,
        });
      } catch (error) {
        if (!isSubscriptionBlockedError(error)) {
          toast.error(error || "File upload failed.");
        }
      }
    }
    if (uploaded.length > 0) {
      setUploadedFiles((prev) => [...prev, ...uploaded]);
      setEnabledSections((prev) => ({ ...prev, files: true }));
    }
    event.target.value = "";
  };

  const removeUploadedFile = (fileId) => {
    setUploadedFiles((prev) => {
      const target = prev.find((x) => x.id === fileId);
      if (target?.url && String(target.url).startsWith("blob:")) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter((x) => x.id !== fileId);
    });
    setPreviewFile((current) => (current?.id === fileId ? null : current));
  };

  const handleFilePreview = (file, kind) => {
    if (kind === "document") {
      window.open(file.url, "_blank", "noopener,noreferrer");
      return;
    }
    setPreviewFile({ ...file, kind });
  };

  const enableChecklistsSection = () => {
    setEnabledSections((prev) => ({ ...prev, checklists: true }));
    setChecklists((prev) => (prev.length === 0 ? [createChecklistTemplate()] : prev));
  };

  const toggleSection = (key) => {
    setEnabledSections((prev) => {
      const nextEnabled = !prev[key];
      if (key === "checklists" && nextEnabled) {
        setChecklists((list) => (list.length === 0 ? [createChecklistTemplate()] : list));
      }
      return { ...prev, [key]: nextEnabled };
    });
  };

  const toggleVisibleField = (key) => {
    setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleChecklistExpand = (checklistId) => {
    setChecklists((prev) =>
      prev.map((x) => (x.id === checklistId ? { ...x, expanded: !x.expanded } : x))
    );
  };

  const submitChecklistAutosave = async (nextChecklists) => {
    if (!checklistOnlyAutosave || !canSubmit || !onSubmit) return;
    if (checklistAutosavingRef.current) {
      queuedChecklistAutosaveRef.current = nextChecklists;
      return;
    }
    const payload = buildSubmitPayload({ checklists: nextChecklists });
    if (!payload) return;
    try {
      checklistAutosavingRef.current = true;
      setChecklistAutosaving(true);
      await onSubmit(payload);
    } catch (_) {
      // Parent shows the toast; leave the local edit visible so the user can retry.
    } finally {
      checklistAutosavingRef.current = false;
      setChecklistAutosaving(false);
      const queued = queuedChecklistAutosaveRef.current;
      queuedChecklistAutosaveRef.current = null;
      if (queued) {
        setTimeout(() => {
          void submitChecklistAutosave(queued);
        }, 0);
      }
    }
  };

  const toggleChecklistItem = (checklistId, itemId, done) => {
    const buildNext = (prev) =>
      prev.map((checklist) =>
        checklist.id === checklistId
          ? {
              ...checklist,
              items: checklist.items.map((item) =>
                item.id === itemId ? { ...item, done } : item
              ),
            }
          : checklist
      );
    const next = buildNext(latestChecklistsRef.current);
    latestChecklistsRef.current = next;
    setChecklists(next);
    if (checklistOnlyAutosave) {
      void submitChecklistAutosave(next);
    }
  };

  const removeChecklistItem = (checklistId, itemId) => {
    const buildNext = (prev) =>
      prev.map((checklist) =>
        checklist.id === checklistId
          ? { ...checklist, items: checklist.items.filter((item) => item.id !== itemId) }
          : checklist
      );
    const next = buildNext(latestChecklistsRef.current);
    latestChecklistsRef.current = next;
    setChecklists(next);
  };

  const updateChecklistItemText = (checklistId, itemId, text) => {
    const buildNext = (prev) =>
      prev.map((checklist) =>
        checklist.id === checklistId
          ? {
              ...checklist,
              items: checklist.items.map((item) =>
                item.id === itemId ? { ...item, text } : item
              ),
            }
          : checklist
      );
    const next = buildNext(latestChecklistsRef.current);
    latestChecklistsRef.current = next;
    setChecklists(next);
  };

  const addChecklistItem = (checklistId) => {
    const buildNext = (prev) =>
      prev.map((checklist) =>
        checklist.id === checklistId
          ? {
              ...checklist,
              items: [
                ...checklist.items,
                { id: `i_${Math.random()}`, text: "", done: false },
              ],
            }
          : checklist
      );
    const next = buildNext(latestChecklistsRef.current);
    latestChecklistsRef.current = next;
    setChecklists(next);
  };

  const buildSubmitPayload = (overrides = {}) => {
    const nextChecklists = overrides.checklists || checklists;
    const safeRecurringConfig =
      entityType === "task" && mode === "create" && initialData == null
        ? null
        : showRecurringTaskControls && recurringConfig
          ? prepareRecurringDraftForSave(recurringConfig)
          : null;
    const safeEnabledSections =
      entityType === "task" && mode === "create" && initialData == null
        ? { ...enabledSections, recurring: false }
        : showRecurringTaskControls
          ? enabledSections
          : { ...enabledSections, recurring: false };
    const trimmedStatus = (status || "").trim();
    const resolvedStatus = trimmedStatus || (mode === "create" ? "Pending" : trimmedStatus);

    const persistFiles = safeEnabledSections.files;
    const persistChecklists = safeEnabledSections.checklists;
    const submitEnabledSections = {
      ...safeEnabledSections,
      files: persistFiles,
      checklists: persistChecklists,
      participants:
        entityType === "task" ? false : safeEnabledSections.participants === true,
    };

    const taskParticipantsOff =
      entityType === "task"
        ? { participants: [], enabledSections: submitEnabledSections }
        : { participants, enabledSections: submitEnabledSections };

    /**
     * Templates carry a relative `deadlineOffset` (Bitrix-style); tasks keep
     * their absolute datetime exactly as before.
     */
    const deadlineFields =
      entityType === "template"
        ? { deadline: null, deadlineOffset: normalizeOffsetInput(deadlineOffset) }
        : {
            deadline: (() => {
              if (
                entityType === "task" &&
                mode === "edit" &&
                initialData?._id &&
                !deadlineTouchedByUserRef.current
              ) {
                return resolveServerDeadlineIso(initialData);
              }
              return deadline ? dayjs(deadline).toISOString() : null;
            })(),
          };
    const payload = {
      name: name.trim(),
      description,
      templateType: templateType.trim(),
      status: resolvedStatus,
      priority,
      owners,
      assignees,
      ...taskParticipantsOff,
      ...deadlineFields,
      files: persistFiles
        ? uploadedFiles.map((f) => ({
            url: f.url,
            filename: f.filename || f.id,
            originalName: f.originalName || f.name,
            size: f.size || 0,
            mimetype: f.type || "",
            mediaType: f.mediaType || detectMediaType(f.type),
          }))
        : [],
      checklists: persistChecklists
        ? nextChecklists.map((checklist) => ({
            title: checklist.title,
            items: checklist.items.map((item) => ({
              text: item.text || "",
              done: Boolean(item.done),
            })),
          }))
        : [],
      recurringConfig: safeRecurringConfig,
      ...(entityType === "task"
        ? { deadlineDisplayUtcOffsetMinutes: -new Date().getTimezoneOffset() }
        : {}),
    };
    if (!payload.name) {
      toast.error(entityType === "task" ? "Task name is required." : "Template name is required.");
      return null;
    }
    if (
      safeEnabledSections.recurring &&
      safeRecurringConfig &&
      safeRecurringConfig.recurringStopMode === "exactDate"
    ) {
      const startD = dayjs(safeRecurringConfig.recurringStartDate).startOf("day");
      const endD = dayjs(safeRecurringConfig.recurringStopExactDate).startOf("day");
      if (startD.isValid() && endD.isValid() && endD.isBefore(startD)) {
        toast.error("Recurrence end date cannot be before the recurrence start date.");
        return null;
      }
    }
    if (safeEnabledSections.recurring && safeRecurringConfig) {
      const dailyCheck = validateDailyRecurringDraft(safeRecurringConfig);
      if (!dailyCheck.ok) {
        toast.error(dailyCheck.message);
        return null;
      }
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!canSubmit || !onSubmit) return;
    const payload = buildSubmitPayload();
    if (!payload) return;
    await onSubmit(payload);
    deadlineTouchedByUserRef.current = false;
  };

  const syncFormFromServerTask = (task) => {
    if (!task || entityType !== "task") return;
    const serverStatus = String(task.status ?? "").trim();
    if (serverStatus) setStatus(serverStatus);
    if (!deadlineTouchedByUserRef.current) {
      setDeadline(toPickerDayjs(task.deadlineAt ?? task.deadline ?? null));
    }
  };

  const handleMarkTaskComplete = async () => {
    if (entityType !== "task" || mode !== "edit" || !taskAccessFlags?.canUpdateStatus) return;
    if (onCompleteTask) {
      const refreshed = await onCompleteTask();
      syncFormFromServerTask(refreshed);
      return;
    }
    if (!canSubmit || !onSubmit || !taskAccessFlags?.canFullEdit) return;
    const payload = buildSubmitPayload();
    if (!payload) return;
    const updated = await onSubmit({ ...payload, status: "Complete" });
    syncFormFromServerTask(updated);
  };

  const taskEditFooter =
    entityType === "task" && !isViewMode && mode === "edit";
  const ownerTaskEditFooter = taskEditFooter && !!taskAccessFlags?.canFullEdit;
  const assigneeTaskEditFooter =
    taskEditFooter && !taskAccessFlags?.canFullEdit && !!taskAccessFlags?.canEditChecklists;
  const statusKey = String(status || "")
    .trim()
    .toLowerCase();
  const showCompleteButton =
    taskEditFooter &&
    !!taskAccessFlags?.canUpdateStatus &&
    statusKey !== "complete" &&
    statusKey !== "completed";

  const chatTaskId = entityType === "task" && initialData?._id ? String(initialData._id) : null;

  /** Live task people for @ mentions in task chat (owners/assignees/participants + creator). */
  const taskPeopleForMentions = useMemo(() => {
    if (entityType !== "task") return null;
    return {
      owners,
      assignees,
      participants: [],
      createdBy: initialData?.createdBy,
    };
  }, [entityType, owners, assignees, initialData?.createdBy]);

  const taskChatSummary = useMemo(() => {
    if (entityType !== "task") return null;
    const serverDeadlineRaw = initialData?.deadlineAt ?? initialData?.deadline ?? null;
    const overviewDeadlineSource =
      !deadlineTouchedByUserRef.current && serverDeadlineRaw
        ? toPickerDayjs(serverDeadlineRaw)
        : deadline;
    const deadlineLabel =
      overviewDeadlineSource && dayjs(overviewDeadlineSource).isValid()
        ? dayjs(overviewDeadlineSource).format(`${TASK_DEADLINE_DATE_FORMAT} h:mm A`)
        : "—";
    const createdAtLabel =
      initialData?.createdAt && dayjs(initialData.createdAt).isValid()
        ? dayjs(initialData.createdAt).format("MMM D, YYYY h:mm A")
        : "—";
    return {
      title: (name || "").trim() || initialData?.name || "Task",
      type: (templateType || "").trim() || "—",
      status: status || "—",
      priority: priority || "—",
      deadline: deadlineLabel,
      createdBy: initialData?.createdBy?.name || currentUserName,
      createdAt: createdAtLabel,
      owners: owners.length ? owners.map((o) => o.name).filter(Boolean).join(", ") : "—",
      assignees: assignees.length ? assignees.map((a) => a.name).filter(Boolean).join(", ") : "—",
      recurrence:
        showRecurringTaskControls && enabledSections.recurring && recurringConfig
          ? buildRecurringSummary(recurringConfig)
          : null,
    };
  }, [
    entityType,
    name,
    initialData?.name,
    initialData?.createdBy?.name,
    templateType,
    status,
    priority,
    deadline,
    initialData?.deadlineAt,
    initialData?.deadline,
    initialData?.createdAt,
    owners,
    assignees,
    currentUserName,
    showRecurringTaskControls,
    enabledSections.recurring,
    recurringConfig,
  ]);

  const [taskChatConnectionStatus, setTaskChatConnectionStatus] = useState({
    isConnected: true,
    isReconnecting: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 2,
  });

  useEffect(() => {
    if (!open || !splitWithChat || entityType !== "task") return undefined;

    const tick = () => {
      try {
        const status = getConnectionStatus();
        if (status && typeof status === "object" && !status.message) {
          setTaskChatConnectionStatus(status);
        }
      } catch (_) {
        setTaskChatConnectionStatus({
          isConnected: false,
          isReconnecting: false,
          reconnectAttempts: 0,
          maxReconnectAttempts: 2,
        });
      }
    };

    const onOffline = () =>
      setTaskChatConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: 0,
        maxReconnectAttempts: 2,
      });

    tick();
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", tick);

    // Direct subscription to the connection monitor's real events, replacing
    // the previous 1s poll — this is what keeps isConnected/isReconnecting/the
    // attempt counter live and correct (the poll never reliably caught a
    // successful manual retryConnection(), leaving the banner stuck stale).
    const handleConnected = () => {
      setTaskChatConnectionStatus((prev) => ({ ...prev, isConnected: true, isReconnecting: false, reconnectAttempts: 0 }));
    };
    const handleDisconnected = () => {
      setTaskChatConnectionStatus((prev) => ({ ...prev, isConnected: false, isReconnecting: true }));
    };
    const handleReconnecting = (data) => {
      setTaskChatConnectionStatus((prev) => ({
        ...prev,
        isConnected: false,
        isReconnecting: true,
        reconnectAttempts: data?.reconnectAttempts ?? prev.reconnectAttempts,
        maxReconnectAttempts: data?.maxReconnectAttempts ?? prev.maxReconnectAttempts,
      }));
    };
    const handleReconnectFailed = (data) => {
      const status = getConnectionStatus();
      setTaskChatConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: status.reconnectAttempts || (data?.reconnectAttempts ?? 0),
        maxReconnectAttempts: status.maxReconnectAttempts || (data?.maxReconnectAttempts ?? 2),
      });
    };

    onConnectionChange(handleConnected);
    onDisconnectionChange(handleDisconnected);
    onReconnectingChange(handleReconnecting);
    onReconnectFailedChange(handleReconnectFailed);

    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", tick);
      connectionMonitor.off('connection', handleConnected);
      connectionMonitor.off('disconnection', handleDisconnected);
      connectionMonitor.off('reconnecting', handleReconnecting);
      connectionMonitor.off('reconnectFailed', handleReconnectFailed);
    };
  }, [open, splitWithChat, entityType]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        TransitionComponent={SlideUpTransition}
        maxWidth={splitWithChat && entityType === "task" ? false : "lg"}
        sx={
          splitWithChat && entityType === "task"
            ? { "& .MuiDialog-container": { alignItems: "flex-end" } }
            : undefined
        }
        PaperProps={{
          sx: taskFormPaperSx({
            extra:
              splitWithChat && entityType === "task"
                ? {
                    maxWidth: "min(1760px, 99vw) !important",
                    width: "100%",
                    m: 0,
                    minHeight: "95vh",
                    maxHeight: "97vh",
                    borderRadius: "16px 16px 0 0",
                  }
                : { maxWidth: "min(920px, 96vw) !important" },
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "stretch",
              width: "100%",
              flex: 1,
              minHeight: 0,
            }}
          >
          <Box
            sx={{
              ...(splitWithChat && entityType === "task" ? taskFormColumnSplitFormSx : { flex: 1 }),
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
              ...taskFormDarkContainerSx,
            }}
          >
          {splitWithChat && entityType === "task" && (
            <ConnectionBanner
              connectionStatus={taskChatConnectionStatus}
              onRetry={() => retryConnection()}
              sx={{ margin: 0, flexShrink: 0 }}
            />
          )}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: { xs: 1, sm: 1.25 },
            pt: { xs: 2, sm: 2.5 },
            px: { xs: 1.75, sm: 2 },
            flexShrink: 0,
          }}
        >
          <TextField
            variant="standard"
            placeholder={entityType === "task" ? "Task name" : "Template name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!taskDetailFieldEdit}
            sx={taskFormNameInputSx}
          />
          {/* Split-chat Task layout: the chat pane (real header when editing, placeholder
              when creating) has its own close button — showing this one too would duplicate
              it. Keep this one only for Create (chat pane is just a placeholder there), hide
              it for View/Edit (chat pane already has a clear, working close button). */}
          {(!(splitWithChat && entityType === "task") || isTaskCreateMode) && (
            <IconButton onClick={onClose} aria-label="Close" edge="end" sx={{ flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers sx={taskFormScrollContentSx}>
          <Stack spacing={1.75}>
            <Box
              sx={{
                ...taskFormSectionSx,
                p: taskFormSectionPadding,
                borderColor: "rgba(14, 144, 70, 0.45)",
              }}
            >
              <Stack spacing={2.25}>
                <Box sx={taskFormSectionTitleWithIconSx}>
                  <ListAltOutlinedIcon sx={{ color: "primary.main" }} />
                  <Typography sx={taskFormSectionTitleSx}>Details</Typography>
                </Box>
                <Box sx={taskFormFieldRowSx}>
                  <Typography sx={taskFormFieldLabelSx}>Owner:</Typography>
                  <Typography sx={taskFormBodyTextSx}>{ownerDisplayText}</Typography>
                </Box>
                <Box sx={taskFormFieldRowSx}>
                  <Typography sx={taskFormFieldLabelSx}>Assignee:</Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ flex: 1, alignItems: "center" }}>
                    <Typography sx={taskFormBodyTextSx}>
                      {assignees.length > 0
                        ? assignees.map((item) => item.name).join(", ")
                        : "No assignee selected"}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<PersonOutlineIcon />}
                      sx={taskFormInlineButtonSx}
                      onClick={() => setAssigneePickerOpen(true)}
                      disabled={!taskDetailFieldEdit}
                    >
                      {assignees.length > 0 ? "Edit assignees" : "Add assignees"}
                    </Button>
                  </Stack>
                </Box>
                <Box sx={taskFormFieldRowSx}>
                  <Typography sx={taskFormFieldLabelSx}>Deadline:</Typography>
                  {entityType === "template" ? (
                    <TemplateDeadlineOffsetPicker
                      value={deadlineOffset}
                      onChange={setDeadlineOffset}
                      disabled={!taskDetailFieldEdit}
                    />
                  ) : (
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="nowrap" sx={{ minWidth: 0 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          format={TASK_DEADLINE_DATE_FORMAT}
                          value={toPickerDayjs(deadline)}
                          onChange={(newValue) => {
                            deadlineTouchedByUserRef.current = true;
                            setDeadline(mergeDeadlineDate(newValue, deadline));
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              placeholder: "DD/MM/YYYY",
                              sx: {
                                width: 160,
                                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                              },
                              disabled: !taskDetailFieldEdit,
                            },
                          }}
                        />
                        <TimePicker
                          value={toPickerDayjs(deadline)}
                          onChange={(newValue) => {
                            deadlineTouchedByUserRef.current = true;
                            setDeadline(mergeDeadlineTime(newValue, deadline));
                          }}
                          disabled={!taskDetailFieldEdit || !toPickerDayjs(deadline)}
                          ampm
                          views={["hours", "minutes"]}
                          minutesStep={5}
                          slotProps={{
                            textField: {
                              size: "small",
                              placeholder: "Select time",
                              sx: {
                                width: 145,
                                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Box>

            <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
              <TextField
                multiline
                minRows={4}
                fullWidth
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="standard"
                disabled={!taskDetailFieldEdit}
                InputProps={{ disableUnderline: true }}
              />

              {uploadedFiles.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {uploadedFiles.map((file) => (
                    <TaskFormFileTile
                      key={file.id}
                      file={file}
                      onPreview={handleFilePreview}
                      onRemove={removeUploadedFile}
                      canDelete={taskDetailFieldEdit}
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 0.75 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={!taskDetailFieldEdit}>
                  <AttachFileIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (!taskChecklistEdit) return;
                    enableChecklistsSection();
                  }}
                  disabled={!taskChecklistEdit}
                >
                  <ChecklistIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>


            {visibleFields.type && (
              <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
                <Box sx={taskFormFieldRowStartSx}>
                  <Box sx={{ ...taskFormFieldLabelWithIconSx, alignSelf: "center" }}>
                    <CategoryOutlinedIcon sx={{ ...taskFormFieldLabelIconSx, color: "#8b5cf6" }} />
                    <Typography sx={taskFormFieldLabelRowSx}>Type:</Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ flex: 1, alignItems: "flex-start", minWidth: 0 }}
                  >
                    <Autocomplete
                      freeSolo
                      options={typeChoices}
                      value={templateType}
                      getOptionLabel={optionLabel}
                      isOptionEqualToValue={isFieldOptionEqualToValue}
                      onChange={handleAutocompleteValueChange(setTemplateType)}
                      disabled={!taskDetailFieldEdit}
                      sx={taskFormAutocompleteSx}
                      renderOption={renderFieldOption("type", typeChoices)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select or type task type"
                          disabled={!taskDetailFieldEdit}
                          onBlur={(e) => {
                            const v = e.target.value?.trim();
                            if (v) setTemplateType(v);
                            params.inputProps?.onBlur?.(e);
                          }}
                        />
                      )}
                    />
                    {(showTemplateFieldOpts || showTaskFieldOpts) && (
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={taskFormInlineButtonSx}
                        disabled={!taskDetailFieldEdit}
                        onClick={() => setManageTypeOpen(true)}
                      >
                        Add new
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}

            {visibleFields.status && (
              <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
                <Box sx={taskFormFieldRowStartSx}>
                  <Box sx={{ ...taskFormFieldLabelWithIconSx, alignSelf: "center" }}>
                    <FlagOutlinedIcon sx={{ ...taskFormFieldLabelIconSx, color: "#0ea5e9" }} />
                    <Typography sx={taskFormFieldLabelRowSx}>Status:</Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ flex: 1, alignItems: "flex-start", minWidth: 0 }}
                  >
                    <Autocomplete
                      freeSolo
                      options={statusChoices}
                      value={status}
                      getOptionLabel={optionLabel}
                      isOptionEqualToValue={isFieldOptionEqualToValue}
                      onChange={handleAutocompleteValueChange(setStatus)}
                      disabled={!taskDetailFieldEdit}
                      sx={taskFormAutocompleteSx}
                      renderOption={renderFieldOption("status", statusChoices)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select or type status"
                          disabled={!taskDetailFieldEdit}
                          onBlur={(e) => {
                            const v = e.target.value?.trim();
                            if (v) setStatus(v);
                            params.inputProps?.onBlur?.(e);
                          }}
                        />
                      )}
                    />
                    {(showTemplateFieldOpts || showTaskFieldOpts) && (
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={taskFormInlineButtonSx}
                        disabled={!taskDetailFieldEdit}
                        onClick={() => setManageStatusOpen(true)}
                      >
                        Add new
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}

            {visibleFields.priority && (
              <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
                <Box sx={taskFormFieldRowStartSx}>
                  <Box sx={{ ...taskFormFieldLabelWithIconSx, alignSelf: "center" }}>
                    <LowPriorityIcon sx={{ ...taskFormFieldLabelIconSx, color: "#ef4444" }} />
                    <Typography sx={taskFormFieldLabelRowSx}>Priority:</Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ flex: 1, alignItems: "flex-start", minWidth: 0 }}
                  >
                    <Autocomplete
                      freeSolo
                      options={priorityChoices}
                      value={priority}
                      getOptionLabel={optionLabel}
                      isOptionEqualToValue={isFieldOptionEqualToValue}
                      onChange={handleAutocompleteValueChange(setPriority)}
                      disabled={!taskDetailFieldEdit}
                      sx={taskFormAutocompleteSx}
                      renderOption={renderFieldOption("priority", priorityChoices)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select or type priority"
                          disabled={!taskDetailFieldEdit}
                          onBlur={(e) => {
                            const v = e.target.value?.trim();
                            if (v) setPriority(v);
                            params.inputProps?.onBlur?.(e);
                          }}
                        />
                      )}
                    />
                    {(showTemplateFieldOpts || showTaskFieldOpts) && (
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={taskFormInlineButtonSx}
                        disabled={!taskDetailFieldEdit}
                        onClick={() => setManagePriorityOpen(true)}
                      >
                        Add new
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}

            {enabledSections.files && (
              <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
                <Box
                  onClick={() => taskDetailFieldEdit && fileInputRef.current?.click()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: taskDetailFieldEdit ? "pointer" : "default",
                    borderRadius: "8px",
                    mx: -0.5,
                    px: 0.5,
                    py: 0.25,
                    "&:hover": taskDetailFieldEdit
                      ? { bgcolor: (theme) => theme.palette.action.hover }
                      : {},
                  }}
                >
                  <Box sx={{ ...taskFormSectionTitleWithIconSx, mb: 0 }}>
                    <AttachFileIcon sx={{ color: "#f59e0b" }} />
                    <Typography sx={taskFormSectionTitleSx}>Files: {uploadedFiles.length}</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); taskDetailFieldEdit && fileInputRef.current?.click(); }}
                    disabled={!taskDetailFieldEdit}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {uploadedFiles.map((file) => (
                      <TaskFormFileTile
                        key={`f_${file.id}`}
                        file={file}
                        onPreview={handleFilePreview}
                        onRemove={removeUploadedFile}
                        canDelete={taskDetailFieldEdit}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {enabledSections.checklists && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  pt: 0,
                  pb: 1.5,
                  px: 0,
                  borderRadius: "12px",
                  overflow: "hidden",
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "#121212" : "#f1f4f6"),
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.22)" : "#e2e5e9"}`,
                }}
              >
                {completedChecklistMeta.map((checklist, checklistIndex) => {
                  const progressPct =
                    checklist.total > 0
                      ? Math.round((checklist.completed / checklist.total) * 100)
                      : 0;
                  return (
                  <Box
                    key={checklist.id}
                    sx={{
                      ...taskFormSectionSx,
                      p: { xs: 1.75, sm: 2 },
                      border: "none",
                      boxShadow: "none",
                      borderTopLeftRadius: checklistIndex === 0 ? 0 : "10px",
                      borderTopRightRadius: checklistIndex === 0 ? 0 : "10px",
                      borderBottomLeftRadius: "10px",
                      borderBottomRightRadius: "10px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1, minWidth: 0 }}>
                        <ChecklistIcon sx={{ color: "#6366f1", fontSize: 21, flexShrink: 0, mt: "3px" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.05rem",
                              lineHeight: 1.3,
                              color: (theme) => (theme.palette.mode === "dark" ? "#f1f5f9" : "#111827"),
                            }}
                          >
                            {checklist.title}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.45 }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                                color: (theme) =>
                                  theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.55)" : "#9e9e9e",
                              }}
                            >
                              Completed {checklist.completed} out of {checklist.total}
                            </Typography>
                            <Box
                              sx={{
                                width: 72,
                                height: 5,
                                borderRadius: 999,
                                flexShrink: 0,
                                overflow: "hidden",
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.09)",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${progressPct}%`,
                                  height: "100%",
                                  borderRadius: 999,
                                  bgcolor: (theme) => theme.palette.primary.main,
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <IconButton size="small" sx={{ mt: -0.25 }} onClick={() => toggleChecklistExpand(checklist.id)}>
                        {checklist.expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                    {checklist.expanded && (
                      <Box sx={{ mt: 0.75 }}>
                        {checklist.items.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "auto 1fr auto",
                              gap: 1,
                              alignItems: "center",
                              py: 1.1,
                              borderTop: (theme) =>
                                `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.07)"}`,
                            }}
                          >
                            <Checkbox
                              checked={item.done}
                              onChange={(e) =>
                                toggleChecklistItem(checklist.id, item.id, e.target.checked)
                              }
                              disabled={!taskChecklistEdit || checklistAutosaving}
                              size="small"
                              sx={{ p: 0.25 }}
                            />
                            {taskChecklistEdit && !isViewMode ? (
                              <TextField
                                variant="standard"
                                fullWidth
                                placeholder="Checklist item"
                                value={item.text}
                                onChange={(e) =>
                                  updateChecklistItemText(checklist.id, item.id, e.target.value)
                                }
                                disabled={checklistAutosaving}
                                InputProps={{ disableUnderline: true }}
                                sx={{
                                  "& .MuiInputBase-input": {
                                    textDecoration: item.done ? "line-through" : "none",
                                    fontSize: "0.9rem",
                                    py: 0,
                                  },
                                }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: "0.9rem", textDecoration: item.done ? "line-through" : "none" }}>
                                {item.text || "—"}
                              </Typography>
                            )}
                            {taskDetailFieldEdit && (
                              <IconButton
                                size="small"
                                onClick={() => removeChecklistItem(checklist.id, item.id)}
                                disabled={checklistAutosaving}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                        {taskChecklistEdit && !isViewMode && (
                          <Box
                            sx={{
                              borderTop: (theme) =>
                                `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.07)"}`,
                            }}
                          >
                            <Button
                              startIcon={<AddIcon />}
                              sx={{ textTransform: "none", mt: 0.75, fontSize: "0.85rem", alignSelf: "flex-start" }}
                              onClick={() => addChecklistItem(checklist.id)}
                              disabled={checklistAutosaving}
                            >
                              Add item
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  );
                })}
                <Button
                  startIcon={<AddIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    enableChecklistsSection();
                    setOpenChecklistBuilder(true);
                  }}
                  disabled={!taskChecklistEdit || checklistAutosaving}
                >
                  Add new checklist
                </Button>
              </Box>
            )}

            {showRecurringTaskControls && enabledSections.recurring && recurringConfig && (
              <Box sx={{ ...taskFormSectionSx, p: taskFormSectionPadding }}>
                <Box
                  sx={{
                    ...taskFormFieldRowSx,
                    flexDirection: { xs: "column", sm: "row" },
                    color: "text.primary",
                  }}
                >
                  <Box sx={{ ...taskFormFieldLabelWithIconSx, gap: 0.85, flexShrink: 0 }}>
                    <AutorenewIcon sx={{ color: "#14b8a6", fontSize: 19, flexShrink: 0 }} />
                    <Typography sx={{ ...taskFormSectionTitleSx, mb: 0 }}>
                      Recurrence:
                    </Typography>
                  </Box>
                  <Box sx={taskFormRecurrenceInnerSx}>
                    <Typography sx={taskFormRecurrenceSummarySx}>
                      {buildRecurringSummary(recurringConfig)}
                    </Typography>
                  <IconButton
                    size="medium"
                    className="recurrence-remove-btn"
                    onClick={() => {
                      setRecurringConfig(null);
                      setEnabledSections((prev) => ({ ...prev, recurring: false }));
                      if (entityType === "task" && mode !== "create" && initialData?._id) {
                        setRecurringRemovedThisSession(true);
                      }
                    }}
                    disabled={!taskDetailFieldEdit}
                    sx={{
                      p: 0.05,
                      position: "absolute",
                      top: -6,
                      right: -6,
                      ...taskFormFloatingButtonSx,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pt: 0.25 }}>
              <Chip
                icon={<CategoryOutlinedIcon />}
                label="Type"
                clickable
                variant="outlined"
                onClick={() => toggleVisibleField("type")}
                disabled={!taskDetailFieldEdit}
                sx={taskFormChipSx(visibleFields.type)}
              />
              <Chip
                icon={<FlagOutlinedIcon />}
                label="Status"
                clickable
                variant="outlined"
                onClick={() => toggleVisibleField("status")}
                disabled={!taskDetailFieldEdit}
                sx={taskFormChipSx(visibleFields.status)}
              />
              <Chip
                icon={<LowPriorityIcon />}
                label="Priority"
                clickable
                variant="outlined"
                onClick={() => toggleVisibleField("priority")}
                disabled={!taskDetailFieldEdit}
                sx={taskFormChipSx(visibleFields.priority)}
              />
              <Chip
                icon={<AttachFileIcon />}
                label="Files"
                clickable
                variant="outlined"
                onClick={() => toggleSection("files")}
                disabled={!taskDetailFieldEdit}
                sx={taskFormChipSx(enabledSections.files)}
              />
              <Chip
                icon={<ChecklistIcon />}
                label="Checklists"
                clickable
                variant="outlined"
                onClick={() => toggleSection("checklists")}
                disabled={!taskChecklistEdit}
                sx={taskFormChipSx(enabledSections.checklists)}
              />
              {showRecurringTaskControls && (
                <Chip
                  icon={<AutorenewIcon />}
                  label="Recurring Task"
                  clickable
                  variant="outlined"
                  onClick={() => setOpenRecurringDialog(true)}
                  disabled={!taskDetailFieldEdit}
                  sx={taskFormChipSx(enabledSections.recurring)}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={taskFormStickyFooterSx}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
            {isViewMode ? (
              canSwitchToEdit ? (
                <Button
                  variant="contained"
                  sx={taskFormPrimaryButtonSx}
                  onClick={() => onSwitchToEdit?.()}
                >
                  Edit
                </Button>
              ) : null
            ) : ownerTaskEditFooter || assigneeTaskEditFooter ? (
              <>
                {showCompleteButton ? (
                  <Button
                    variant="contained"
                    color="success"
                    sx={taskFormPrimaryButtonSx}
                    onClick={handleMarkTaskComplete}
                    disabled={submitting || checklistAutosaving}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {submitting ? "Saving..." : "Complete"}
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  sx={taskFormPrimaryButtonSx}
                  onClick={handleSubmit}
                  disabled={submitting || checklistAutosaving || !canSubmit}
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {submitting ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                sx={taskFormPrimaryButtonSx}
                onClick={handleSubmit}
                disabled={submitting || checklistAutosaving || !canSubmit}
                startIcon={submitting || checklistAutosaving ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {submitting || checklistAutosaving ? "Saving..." : mode === "edit" ? "Update" : "Create"}
              </Button>
            )}
            <Button onClick={onClose} sx={taskFormSecondaryButtonSx}>
              Cancel
            </Button>
          </Box>
          {entityType === "task" && showTemplatesPicker && !isViewMode && canEdit && (
            <>
              <Button
                variant="outlined"
                sx={taskFormSecondaryButtonSx}
                onClick={() => setTemplatesPickerOpen(true)}
              >
                Templates
              </Button>
              <Dialog
                open={templatesPickerOpen}
                onClose={() => {
                  setTemplatesPickerOpen(false);
                  setTemplateSearchQuery("");
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: (theme) => ({
                    maxHeight: "min(82vh, 720px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    ...(theme.palette.mode === "dark" && {
                      border: "1px solid rgba(255, 255, 255, 0.14)",
                      boxSizing: "border-box",
                    }),
                  }),
                }}
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography component="span" variant="h6" fontWeight={600}>
                    Choose a template
                  </Typography>
                  <IconButton
                    aria-label="Close"
                    onClick={() => {
                      setTemplatesPickerOpen(false);
                      setTemplateSearchQuery("");
                    }}
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "0 0 auto",
                    overflow: "hidden",
                    pb: 1,
                  }}
                >
                  <Box sx={{ px: 3, pt: 0.5, pb: 1.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      placeholder="Search templates"
                      value={templateSearchQuery}
                      onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    />
                  </Box>
                  {filteredTemplatePickerRows.length === 0 ? (
                    <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        {(templatePickerRows || []).length === 0
                          ? "No templates available."
                          : "No templates match your search."}
                      </Typography>
                    </Box>
                  ) : (
                    <List
                      dense
                      disablePadding
                      sx={{
                        overflow: "auto",
                        maxHeight: "min(50vh, 360px)",
                        px: 1,
                      }}
                    >
                      {filteredTemplatePickerRows.map((row) => (
                        <ListItemButton
                          key={row.id}
                          onClick={async () => {
                            setTemplatesPickerOpen(false);
                            setTemplateSearchQuery("");
                            if (onApplyTemplateFromPicker) {
                              await onApplyTemplateFromPicker(row.id);
                            }
                          }}
                          sx={{
                            borderRadius: 1,
                            mx: 1,
                            py: 1.25,
                          }}
                        >
                          <ListItemText
                            primary={row.name}
                            primaryTypographyProps={{ variant: "body1" }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              </Dialog>
            </>
          )}
        </DialogActions>
          </Box>
          {splitWithChat && entityType === "task" && (
            <Box
              sx={{
                ...taskFormColumnSplitChatSx,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
            <TaskChatPanel
              taskId={chatTaskId}
              memberCount={0}
              compact
              onClose={onClose}
              taskSummary={taskChatSummary}
              taskPeopleForMentions={taskPeopleForMentions}
            />
            </Box>
          )}
        </Box>
        </Box>
      </Dialog>

      <ChecklistBuilderDialog
        open={openChecklistBuilder && taskChecklistEdit}
        onClose={() => setOpenChecklistBuilder(false)}
        onSave={(nextChecklists) => {
          setChecklists(nextChecklists);
          setEnabledSections((prev) => ({ ...prev, checklists: true }));
        }}
        initialChecklists={checklists}
        canDelete={taskDetailFieldEdit}
      />

      <RecurringTaskDialog
        open={
          showRecurringTaskControls &&
          openRecurringDialog &&
          (entityType === "task" ? taskDetailFieldEdit : canEdit)
        }
        onClose={() => setOpenRecurringDialog(false)}
        value={recurringConfig}
        onSave={(config) => {
          setRecurringConfig(config);
          setEnabledSections((prev) => ({ ...prev, recurring: true }));
        }}
      />

      {showTemplateFieldOpts && (
        <>
          <TaskTemplateFieldOptionsDialog
            open={manageTypeOpen}
            onClose={() => setManageTypeOpen(false)}
            kind="type"
          />
          <TaskTemplateFieldOptionsDialog
            open={manageStatusOpen}
            onClose={() => setManageStatusOpen(false)}
            kind="status"
          />
          <TaskTemplateFieldOptionsDialog
            open={managePriorityOpen}
            onClose={() => setManagePriorityOpen(false)}
            kind="priority"
          />
        </>
      )}
      {showTaskFieldOpts && (
        <>
          <TaskFieldOptionsDialog
            open={manageTypeOpen}
            onClose={() => setManageTypeOpen(false)}
            kind="type"
          />
          <TaskFieldOptionsDialog
            open={manageStatusOpen}
            onClose={() => setManageStatusOpen(false)}
            kind="status"
          />
          <TaskFieldOptionsDialog
            open={managePriorityOpen}
            onClose={() => setManagePriorityOpen(false)}
            kind="priority"
          />
        </>
      )}

      <TeamAssigneePickerDialog
        open={assigneePickerOpen && (entityType === "task" ? taskDetailFieldEdit : canEdit)}
        onClose={() => setAssigneePickerOpen(false)}
        onApply={setAssignees}
        multiple={true}
        selectedItems={assigneeSelectedItems}
        title="Select Assignees"
      />

      <TaskFormMediaPreviewModal
        open={Boolean(previewFile)}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        disabled={!taskDetailFieldEdit}
        onChange={handleUploadFiles}
      />
    </>
  );
};

export default TaskTemplateCreateModal;
